import { Reveal } from '@/components/reveal';
import { copy } from '@/content/copy';
import { MARKET_COUNT } from '@/content/facts';
import { gsap } from '@/lib/gsap';
import type { TimelineBuilder } from '@/scroll/use-reveal-on-enter';

type Timeline = Parameters<TimelineBuilder>[0];
type Selector = Parameters<TimelineBuilder>[1];

interface MetricFormat {
  prefix?: string;
  suffix?: string;
  decimals: number;
}

const { dashboard } = copy.gateway;

/** Metrics whose value is the data's rather than a sample figure written in the copy. */
const DERIVED_VALUES = { activeMarkets: MARKET_COUNT } as const;

const metricValue = (metric: (typeof dashboard.metrics)[number]): number =>
  'value' in metric ? metric.value : DERIVED_VALUES[metric.id];

/** 24 points of a rising day, drawn in a 600x160 box; the fill closes the same curve. */
const CHART_LINE =
  'M0 124 C40 118,58 84,96 96 S160 130,204 86 S262 66,304 80 S358 104,400 56 S472 84,516 46 S568 38,600 22';
const CHART_FILL = `${CHART_LINE} L600 160 L0 160Z`;

const formatMetric = (value: number, { prefix = '', suffix = '', decimals }: MetricFormat) =>
  `${prefix}${value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}${suffix}`;

/**
 * Merchant dashboard preview: a header bar with a live mark, three metrics, a volume
 * sparkline and the latest transactions. Static markup; `playDashboard` animates it from the
 * gateway section's timeline. Metrics render their final values so that, under reduced
 * motion, nothing has to count.
 */
export function DashboardMock() {
  return (
    <div className="relative">
      <div aria-hidden className="panel-glow absolute -inset-10 -z-10" />
      <Reveal
        data-panel
        role="group"
        aria-label={dashboard.label}
        className="surface overflow-hidden rounded-3xl"
      >
        <div className="flex items-center justify-between gap-4 border-b border-border px-6 py-4">
          <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-sm font-semibold">{dashboard.title}</span>
            <span className="hud text-[0.58rem] text-muted-foreground/70">{dashboard.sample}</span>
          </span>
          <span className="hud flex items-center gap-2 text-brand-sky">
            <span aria-hidden className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand-sky" />
            {dashboard.live}
          </span>
        </div>

        <div className="grid gap-7 p-6">
          <dl className="m-0 grid grid-cols-3 gap-4">
            {dashboard.metrics.map((metric) => {
              const format: MetricFormat = {
                prefix: 'prefix' in metric ? metric.prefix : undefined,
                suffix: 'suffix' in metric ? metric.suffix : undefined,
                decimals: metric.decimals,
              };
              const value = metricValue(metric);
              return (
                <Reveal key={metric.id} data-metric className="grid gap-1">
                  <dt className="hud text-muted-foreground">{metric.label}</dt>
                  <dd
                    className="m-0 font-mono text-[clamp(1.05rem,2vw,1.5rem)] font-semibold tabular-nums"
                    data-count
                    data-value={value}
                    data-prefix={format.prefix ?? ''}
                    data-suffix={format.suffix ?? ''}
                    data-decimals={format.decimals}
                  >
                    {formatMetric(value, format)}
                  </dd>
                  <dd className="m-0 text-xs text-brand-sky">{metric.delta}</dd>
                </Reveal>
              );
            })}
          </dl>

          <Reveal data-chart className="grid gap-2">
            <span className="hud text-muted-foreground/70">{dashboard.chartLabel}</span>
            <svg
              viewBox="0 0 600 160"
              preserveAspectRatio="none"
              aria-hidden
              className="h-[120px] w-full overflow-visible sm:h-[150px]"
            >
              <defs>
                <linearGradient id="dash-fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="hsl(var(--brand-blue))" stopOpacity="0.42" />
                  <stop offset="1" stopColor="hsl(var(--brand-blue))" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path data-chart-fill d={CHART_FILL} fill="url(#dash-fill)" />
              <path
                data-chart-line
                d={CHART_LINE}
                fill="none"
                stroke="hsl(var(--brand-sky))"
                strokeWidth="2.5"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </Reveal>

          <ul className="m-0 grid list-none gap-2 p-0">
            {dashboard.transactions.map((tx) => (
              <li key={`${tx.method}-${tx.kind}`}>
                <Reveal
                  data-tx
                  className="grid grid-cols-[1fr_auto_auto] items-center gap-4 rounded-xl border border-border/70 bg-background/40 px-4 py-3 text-sm"
                >
                  <span className="font-medium">
                    {tx.method} <span className="text-muted-foreground">{tx.kind}</span>
                  </span>
                  <span className="hud text-muted-foreground">{tx.currency}</span>
                  <span className="hud flex items-center gap-2 text-brand-sky">
                    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-brand-sky" />
                    {tx.status}
                  </span>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  );
}

/**
 * Reveals the dashboard from `at` seconds: the panel rises, the metrics count up from zero
 * while they fade in, the sparkline draws itself and its fill follows, then the transactions
 * land one by one. Call from the gateway section's timeline builder.
 */
export function playDashboard(tl: Timeline, q: Selector, at: number): void {
  tl.to(q('[data-panel]'), { opacity: 1, y: 0, duration: 0.9 }, at)
    .to(q('[data-metric]'), { opacity: 1, y: 0, duration: 0.6, stagger: 0.1 }, at + 0.3)
    .to(q('[data-chart]'), { opacity: 1, y: 0, duration: 0.6 }, at + 0.5)
    .to(q('[data-tx]'), { opacity: 1, y: 0, duration: 0.6, stagger: 0.1 }, at + 0.9);

  q<HTMLElement>('[data-count]').forEach((el) => {
    const format: MetricFormat = {
      prefix: el.dataset.prefix,
      suffix: el.dataset.suffix,
      decimals: Number(el.dataset.decimals ?? 0),
    };
    const counter = { value: 0 };
    tl.to(
      counter,
      {
        value: Number(el.dataset.value),
        duration: 1.6,
        ease: 'power2.out',
        onUpdate: () => {
          el.textContent = formatMetric(counter.value, format);
        },
      },
      at + 0.3,
    );
  });

  const [line] = q<SVGPathElement>('[data-chart-line]');
  if (line) {
    const length = line.getTotalLength();
    // Hidden start states set here, at build time, like every other reveal target.
    gsap.set(line, { strokeDasharray: length, strokeDashoffset: length });
    gsap.set(q('[data-chart-fill]'), { opacity: 0 });
    tl.to(line, { strokeDashoffset: 0, duration: 1.6, ease: 'power2.inOut' }, at + 0.5).to(
      q('[data-chart-fill]'),
      { opacity: 1, duration: 0.8 },
      at + 1.1,
    );
  }
}
